import { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  useMediaQuery,
  Checkbox,
  styled,
} from '@mui/material';
import { faEdit, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  IDeaEditMode,
  FieldRequiredIndicator,
  variables,
  ErrorIcon,
  Tooltip,
  TooltipTrigger,
  StyledButton,
  roles,
  roleLabel,
  formatDate,
} from '../common';
import {
  isEmpty,
  isNil,
  compose as lcompose,
  defaultTo as ldefaultTo,
} from 'lodash/fp';
import { flow } from 'fp-ts/lib/function';

export function RegistrationNo({
  item: { deaNum, editMode, key, msgText, selected, role, expDt, disabled },
  editing,
  save,
  remove,
  select,

  tooltipValue,
  toggleTooltip,
  offTooltip,
  feedback,
}: {
  item: IDeaEditMode;
  save?: (a: IDeaEditMode) => void;
  remove?: (key: string) => void;
  editing?: (key: string) => void;
  select?: ({ checked, key }: { checked: boolean; key: string }) => void;

  tooltipValue: (tooltip: string) => boolean;
  toggleTooltip: (tooltip: string) => void;
  offTooltip: (tooltip: string) => void;
  feedback: string;
}) {
  const [newNo, setNewNo] = useState(deaNum);
  const [newNoIsChanged, setNewNoIsChanged] = useState(false);

  const handleSave = () => {
    save &&
      save({
        key,
        deaNum: newNo,
        editMode: 'Read',
        msgCode: null,
        msgText: null,
        serialNumber: null,
      });

    editMode === 'Create' && setNewNo('');
    setNewNoIsChanged(false);
  };

  useEffect(() => {
    if (
      feedback.endsWith('added') &&
      !!document.querySelector('#new-dea-reg-no')
    ) {
      (document.querySelector('#new-dea-reg-no') as HTMLElement).focus();
    }
  }, [feedback]);

  switch (editMode) {
    case 'Create':
      return (
        <CreateWrapper>
          <DeaRegNoLabel htmlFor={`new-dea-reg-no`} />

          <Tooltip name="requestNewDeaNum" tooltipValue={tooltipValue}>
            <TextField
              id={`new-dea-reg-no`}
              value={newNo}
              onChange={({ target: { value } }) =>
                flow(setNewNo, () => setNewNoIsChanged(true))(value)
              }
              error={newNoIsChanged && isEmpty(newNo)}
              variant="outlined"
            />
          </Tooltip>
          <TooltipTrigger
            name="requestNewDeaNum"
            toggleTooltip={toggleTooltip}
            offTooltip={offTooltip}
          />

          <CenterButton
            variant="contained"
            onClick={handleSave}
            disabled={isEmpty(newNo)}>
            add
          </CenterButton>
        </CreateWrapper>
      );

    case 'Update':
      return (
        <LiWrapper>
          <DeaRegNoLabel htmlFor={key} />
          <TextField
            id={key}
            error={isEmpty(deaNum)}
            value={newNo}
            onChange={({ target: { value } }) =>
              flow(setNewNo, () => setNewNoIsChanged(true))(value)
            }
            variant="outlined"
          />
          <span></span>
          <CenterButton
            variant="contained"
            onClick={handleSave}
            disabled={isEmpty(deaNum)}>
            Update
          </CenterButton>
        </LiWrapper>
      );

    case 'Read':
      return (
        <LiWrapper>
          <DeaRegNoLabel htmlFor="" />
          <span>{deaNum}</span>
          <span></span>
          <ReadActions>
            <StyledButton
              onClick={() => {
                editing && editing(key);
              }}>
              <span>Update</span>
              <FontAwesomeIcon icon={faEdit} size="1x" fixedWidth />
            </StyledButton>

            <StyledButton
              onClick={() => {
                remove && remove(key);
              }}>
              <span>Remove</span>
              <FontAwesomeIcon icon={faTrashAlt} size="1x" fixedWidth />
            </StyledButton>
          </ReadActions>

          {isNil(expDt) ? null : (
            <>
              <DeaRegNoExpiration />
              <span>{formatDate(expDt)}</span>
            </>
          )}

          {!isEmpty(msgText) && (
            <DeaNumMsg>
              <ErrorIcon />
              {msgText}
            </DeaNumMsg>
          )}
        </LiWrapper>
      );

    case 'Select':
      return (
        <LiWrapper>
          <DeaRegNoLabel htmlFor="" />
          <span>{deaNum}</span>
          <ReadActions>
            <Checkbox
              checked={selected}
              disabled={disabled}
              onChange={(_, checked) => select && select({ checked, key })}
              name="selectDeaNum"
              color="primary"
            />
          </ReadActions>
          <span></span>
          {!(expDt === null || expDt === void 0) && (
            <>
              <label style={{ fontSize: '0.8em' }}>Expiration Date:</label>
              <span>{formatDate(expDt)}</span>
            </>
          )}
          <label style={{ fontSize: '0.8em' }}>Role:</label>
          <span style={{ fontSize: '0.8em' }}>
            {lcompose(roleLabel(roles), ldefaultTo(''))(role)}
          </span>

          {!isEmpty(msgText) && (
            <DeaNumMsg>
              <ErrorIcon />
              {msgText}
            </DeaNumMsg>
          )}
        </LiWrapper>
      );

    default:
      return <>NA</>;
  }
}

const wrapper = () => ({
  display: 'grid',
  gridTemplateColumns: '4fr 4fr 28px 2fr',
  gap: `1rem`,
  alignItems: 'center',

  '@media (min-width: 40em)': {
    gridTemplateColumns: '3fr 2fr 28px 1fr',
  },

  '& > label': {
    justifySelf: 'end',
  },

  border: variables.cardBorder,
  borderRadius: variables.cardBorderRadius,

  padding: '0.5rem',

  transition: '0.3s ease',
});

const CreateWrapper = styled('article')(({ theme }) => ({
  ...wrapper(),

  boxSizing: 'border-box',
  '@media (min-width: 40em)': { width: '40rem' },
}));

const LiWrapper = styled('li')(({ theme }) => ({
  ...wrapper(),
}));

const CenterButton = styled(Button)(({ theme }) => ({ justifySelf: 'center' }));

const ReadActions = styled('span')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
}));

const DeaNumMsg = styled('span')(({ theme }) => ({
  gridColumn: '1/-1',
  justifySelf: 'center',
}));

function DeaRegNoLabel({ htmlFor }: { htmlFor: string }) {
  const matchesTabsBreakpoint = useMediaQuery('(min-width:40em)', {
    noSsr: true,
  });

  const label = matchesTabsBreakpoint ? 'DEA Registration #' : 'DEA Reg No';
  return (
    <L htmlFor={htmlFor}>
      {label} <FieldRequiredIndicator />:
    </L>
  );
}

function DeaRegNoExpiration() {
  return <L>Expiration Date:</L>;
}

const L = styled('label')(({ theme }) => ({ fontWeight: 500 }));
